import { z } from "zod";
import type { DrawingComment, PublicDrawingTaskSummary } from "./drawing-state";

/**
 * Entity list thumbnails (PLA-471, docs/plans/entity-list-thumbnails.md):
 * shared contract between the web lists, the API resolve RPC and the
 * drawing-worker render job.
 */

export const ENTITY_THUMBNAIL_TYPES = ["comment", "marking"] as const;
export type EntityThumbnailType = (typeof ENTITY_THUMBNAIL_TYPES)[number];

/** One Comment or Marking as decoded from its collab doc. */
export type ThumbnailEntity =
	| { type: "comment"; comment: DrawingComment }
	| { type: "marking"; marking: PublicDrawingTaskSummary };

export const ENTITY_THUMBNAIL_MAX_DRAWINGS = 20;
export const ENTITY_THUMBNAIL_MAX_ENTITIES = 100;
/** Output edge in px; the list shows 40 px inline and 256 px on hover. */
export const ENTITY_THUMBNAIL_PX = 256;

export const entityThumbnailRefSchema = z.object({
	type: z.enum(ENTITY_THUMBNAIL_TYPES),
	id: z.string().min(1).max(200),
	/** `entityRenderDigest` of the client's live copy; null = unrenderable. */
	digest: z.string().max(128).nullable(),
	/** Client's `ENTITY_RENDER_DIGEST_VERSION`; mismatch → digest ignored. */
	digestVersion: z.number().int().nonnegative(),
});
export type EntityThumbnailRef = z.infer<typeof entityThumbnailRefSchema>;

export const getEntityThumbnailsInputSchema = z
	.object({
		drawings: z
			.array(
				z.object({
					drawingId: z.string().uuid(),
					entities: z
						.array(entityThumbnailRefSchema)
						.min(1)
						.max(ENTITY_THUMBNAIL_MAX_ENTITIES),
				}),
			)
			.min(1)
			.max(ENTITY_THUMBNAIL_MAX_DRAWINGS),
	})
	.refine(
		(input) =>
			input.drawings.reduce((sum, d) => sum + d.entities.length, 0) <=
			ENTITY_THUMBNAIL_MAX_ENTITIES,
		{ message: "Too many entities" },
	);
export type GetEntityThumbnailsInput = z.infer<
	typeof getEntityThumbnailsInputSchema
>;

export type EntityThumbnailStatus =
	| { status: "ready"; url: string }
	/** `staleUrl`: the entity's previous ready image while a new one renders. */
	| { status: "pending"; staleUrl?: string }
	| { status: "unavailable" };

export type EntityThumbnailResult = {
	drawingId: string;
	type: EntityThumbnailType;
	id: string;
} & EntityThumbnailStatus;

export interface GetEntityThumbnailsOutput {
	results: EntityThumbnailResult[];
}

/**
 * SSE event on the project jobs stream once a page job has written rows.
 * Carries only the drawing id: the project broadcast reaches members who may
 * lack access to the entities.
 */
export const ENTITY_THUMBNAILS_READY_EVENT = "entity-thumbnails:ready";
export interface EntityThumbnailsReadyPayload {
	drawingId: string;
}
